// Room 内主区 —— RoomHeader + MessageList + Composer
// 数据：useRoom (storage 持久化 + WS) → BubbleMessage 投影

import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";

import { Composer } from "../components/Composer";
import type { ComposerHandle } from "../components/Composer";
import { MessageList } from "../components/MessageList";
import { RoomHeader } from "../components/RoomHeader";
import { RoomMembersPanel } from "../components/RoomMembersPanel";
import { getRoomAdapters, type RoomAdapter } from "../lib/auth-api";
import { useAuth } from "../lib/use-auth";
import { useRoom } from "../lib/use-room";

export function RoomPage() {
  const { roomId = "default" } = useParams<{ roomId: string }>();
  const [params] = useSearchParams();
  const { user } = useAuth();

  // F4: 登录后 device == user.id（让 agent-host 拿 caller_user_id 拉 pubkey verify 用）
  // 未登录 / 匿名时退回 anon-* device id（无法做审批，但能用普通 IM）
  const device = useMemo(() => {
    const q = params.get("device");
    if (q) return q;
    if (user?.id) return user.id;
    const key = "chatbox.deviceId";
    let cached = localStorage.getItem(key);
    if (!cached) {
      cached = `anon-${Math.random().toString(36).slice(2, 8)}`;
      localStorage.setItem(key, cached);
    }
    return cached;
  }, [params, user?.id]);

  const { messages, peers, status, send, sendAttachment, sendRaw } = useRoom(roomId, device, user);
  const composerRef = useRef<ComposerHandle>(null);

  const bubbles = useMemo(
    () =>
      messages.map((m) => ({
        id: m.id,
        device: m.device,
        text: m.text,
        ts: m.receivedAt,
        fromSelf: m.fromSelf,
        isSystem: m.isSystem,
        isAgent: m.isAgent ?? (m.device === "ai" || m.device.endsWith("-agent")),
        approvalRequest: m.approvalRequest,
        invocationId: m.invocationId,
        isComplete: m.isComplete,
        eofReason: m.eofReason,
        stale: m.stale,
        resizeAck: m.resizeAck,
        attachment: m.attachment,
      })),
    [messages],
  );

  // F3+: @ 自动补全候选 = 历史出现过的 adapter id ∪ 常见默认 (老 fallback)
  const mentionSuggestions = useMemo(() => {
    const seen = new Set<string>();
    for (const m of messages) {
      if (m.isAgent && m.device) seen.add(m.device);
      if (m.approvalRequest?.adapter_id) seen.add(m.approvalRequest.adapter_id);
    }
    for (const d of ["echo", "claude", "codex"]) seen.add(d);
    return Array.from(seen).sort();
  }, [messages]);

  // K7: 拉 Room 内 agent 暴露的 adapter + usages 二级菜单
  const [roomAdapters, setRoomAdapters] = useState<RoomAdapter[]>([]);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const list = await getRoomAdapters(roomId);
      if (!cancelled) setRoomAdapters(list);
    })();
    // 每 30s 刷一次（agent 上下线后 chip 也会跟着变）
    const t = setInterval(async () => {
      const list = await getRoomAdapters(roomId);
      if (!cancelled) setRoomAdapters(list);
    }, 30_000);
    return () => {
      cancelled = true;
      clearInterval(t);
    };
  }, [roomId]);

  // A2: 竖屏成员抽屉 state. 桌面 / 横屏 RoomMembersPanel 常驻, drawerOpen 被 CSS 忽略.
  const [drawerOpen, setDrawerOpen] = useState(false);
  const membersCount =
    (user ? 1 : 0) + peers.length + roomAdapters.length;

  return (
    <div className="room">
      <RoomHeader
        roomId={roomId}
        status={status}
        membersCount={membersCount}
        onMembersClick={() => setDrawerOpen(true)}
      />
      <div className="room__body">
        <section className="room__conversation">
          <MessageList
            messages={bubbles}
            currentUserId={user?.id ?? null}
            sendRaw={sendRaw}
          />
          <Composer
            ref={composerRef}
            disabled={status !== "open"}
            onSend={send}
            onSendAttachment={sendAttachment}
            adapters={roomAdapters}
            mentionSuggestions={mentionSuggestions}
          />
        </section>
        <div
          className={`room-members-wrap ${drawerOpen ? "room-members-wrap--drawer-open" : ""}`}
        >
          <RoomMembersPanel
            peers={peers}
            selfDevice={device}
            user={user}
            adapters={roomAdapters}
            onSelectAgent={(adapterId) => {
              composerRef.current?.focusAgent(adapterId);
              setDrawerOpen(false); // 选了 agent 自动关抽屉, 焦点回 composer
            }}
          />
        </div>
        {drawerOpen && (
          <div
            className="room-members-backdrop"
            onClick={() => setDrawerOpen(false)}
            aria-hidden="true"
          />
        )}
      </div>
    </div>
  );
}
